/**
 * PLOT PLANNER — collaboration data model (pure, no Firebase)
 * ──────────────────────────────────────────────────────────
 * Step 1 of the collaboration feature (see docs/collab-sketch.md).
 *
 * The single-owner planner stores the whole plan as ONE blob at
 * `plannerProgress/{licenceKey}` and overwrites it wholesale on every save — so
 * two editors clobber each other. Collaboration moves a plan into its own
 * document `plans/{planId}` whose CONTENT is arranged so each editable unit is
 * its own Firestore field path, enabling scoped `updateDoc(ref, { path: value })`
 * writes that never touch a field someone else is editing.
 *
 * This module is the translation layer between:
 *   • in-memory STATE — the shape PlotPlanner_Product.jsx renders (arrays), and
 *   • plan CONTENT     — the id-keyed-map shape stored in `plans/{planId}`.
 *
 * It is deliberately Firebase-free (pure JSON in/out) so it is trivially
 * unit-testable and can be mirrored server-side by the future createPlan /
 * seedPlanFromLegacy callables. The Firestore reads/writes live in planStore.js.
 *
 * Design decisions baked in (from the sketch, §0.2–§0.3 / §2):
 *   • Stable-id lists → id-keyed maps + an explicit order array:
 *       cast, rel, factions, dynasty.links.
 *   • Coarse atoms (no per-unit ids) written whole:
 *       factions[].members (positional), beatData per act (positional beats),
 *       dynasty.allegiance (house-NAME keys, unsafe as dotted paths).
 *   • Per-viewer view state is NOT synced: `ui` and `collapsed`, plus the
 *       transient per-card `_collapsed` flag, are stripped on the way to content
 *       and re-defaulted on the way back.
 */

const SHAPES = ['standalone', 'duology', 'series'];

const newId = () => 'x' + Math.random().toString(36).slice(2, 9);
const clone = (x) => (x === undefined ? undefined : JSON.parse(JSON.stringify(x)));

/* ── Field paths for scoped updateDoc writes ─────────────────────────────────
   Pure strings only (no Firebase). planStore.js consumes these. Note which are
   fine-grained (one unit) vs coarse (a whole atom rewritten on any change). */
export const P = {
  // fine-grained
  core: (k) => `core.${k}`,
  field: (id) => `fields.${id}`,
  castItem: (id) => `cast.${id}`,
  castProp: (id, k) => `cast.${id}.${k}`,
  castField: (id, k) => `cast.${id}.f.${k}`,
  castOrder: () => 'castOrder',
  relItem: (id) => `rel.${id}`,
  relProp: (id, k) => `rel.${id}.${k}`,
  relField: (id, k) => `rel.${id}.f.${k}`,
  relOrder: () => 'relOrder',
  linkItem: (id) => `dynasty.links.${id}`,
  linkProp: (id, k) => `dynasty.links.${id}.${k}`,
  linkOrder: () => 'dynasty.linkOrder',
  shape: () => 'shape',
  // coarse (whole atom rewritten)
  faction: (id) => `factions.${id}`,          // incl. its positional members[]
  factionOrder: () => 'factionOrder',
  allegiance: () => 'dynasty.allegiance',     // whole map — house-name keys
  act: (shape, ai) => `beatData.${shape}_${ai}`, // whole beat array for one act
};

/* Metadata fields on a plan doc that are NOT plan content. Written only by the
   (future) Admin-SDK callables — createPlan / invite / remove — never by the
   client content-edit path. Listed here so callers/tests share one source. */
export const PLAN_META_FIELDS = ['ownerUid', 'ownerEmail', 'memberEmails', 'title', 'createdAt', 'updatedAt'];

/* ── Blank in-memory state (mirrors DEFAULT_STATE in PlotPlanner_Product.jsx,
   minus the per-viewer ui/collapsed which this layer never persists). ──────── */
export function defaultPlanState() {
  return {
    core: { title: '', hero: '', world: '' },
    fields: {},
    cast: [
      { id: newId(), badge: '1', protagonist: true, name: '', role: 'Protagonist', tier: 'primary', birthHouse: '', currentHouse: '', f: {} },
      { id: newId(), badge: '2', name: '', role: '', tier: 'secondary', birthHouse: '', currentHouse: '', f: {} },
    ],
    rel: [{ id: newId(), pair: '', f: {} }],
    factions: [{ id: newId(), name: '', type: '', fn: '', head: '', members: [{ n: '', r: '' }] }],
    dynasty: { links: [], allegiance: {}, houseColors: {} },
    beatData: {},
    shape: 'standalone',
    housesEnabled: true,
  };
}

/* Fill defaults over a loaded/partial state so an old or truncated shape never
   crashes the transforms (mirrors hydrate() in the component). */
function hydrateState(loaded) {
  const d = defaultPlanState();
  if (!loaded || typeof loaded !== 'object' || Array.isArray(loaded)) return d;
  const dyn = loaded.dynasty && typeof loaded.dynasty === 'object' ? loaded.dynasty : {};
  return {
    core: { ...d.core, ...(loaded.core || {}) },
    fields: { ...(loaded.fields || {}) },
    cast: Array.isArray(loaded.cast) && loaded.cast.length ? clone(loaded.cast) : d.cast,
    rel: Array.isArray(loaded.rel) && loaded.rel.length ? clone(loaded.rel) : d.rel,
    factions: Array.isArray(loaded.factions) && loaded.factions.length ? clone(loaded.factions) : d.factions,
    dynasty: {
      links: Array.isArray(dyn.links) ? clone(dyn.links) : [],
      allegiance: (dyn.allegiance && typeof dyn.allegiance === 'object') ? clone(dyn.allegiance) : {},
      houseColors: (dyn.houseColors && typeof dyn.houseColors === 'object') ? clone(dyn.houseColors) : {},
    },
    beatData: { ...(loaded.beatData || {}) },
    shape: SHAPES.includes(loaded.shape) ? loaded.shape : 'standalone',
    housesEnabled: loaded.housesEnabled !== false, // absent → on
  };
}

/* Strip the transient per-card collapse flag before storing an item. The card's
   collapsed state is per-viewer UI, never shared. */
function stripTransient(item) {
  const out = clone(item) || {};
  delete out._collapsed;
  return out;
}

/* Turn an ordered array of {id,...} items into an id-keyed map + order array.
   Invariant: map[id] === item AND item.id === id (id kept inside the value so a
   unit fetched alone still knows its id and matches the array-item shape). */
function listToMap(arr) {
  const map = {};
  const order = [];
  (Array.isArray(arr) ? arr : []).forEach((item) => {
    if (!item || !item.id) return;
    map[item.id] = stripTransient(item);
    order.push(item.id);
  });
  return { map, order };
}

/* Rebuild an ordered array from an id-keyed map + order array. Tolerant:
   ids in `order` that are missing from `map` are skipped; ids in `map` that are
   absent from `order` are appended (sorted) so nothing is silently lost. */
function mapToList(map, order) {
  const m = (map && typeof map === 'object') ? map : {};
  const seen = new Set();
  const out = [];
  (Array.isArray(order) ? order : []).forEach((id) => {
    if (m[id] && !seen.has(id)) { out.push(clone(m[id])); seen.add(id); }
  });
  Object.keys(m).sort().forEach((id) => {
    if (!seen.has(id)) { out.push(clone(m[id])); seen.add(id); }
  });
  return out;
}

/* ── STATE → CONTENT ─────────────────────────────────────────────────────────
   The shape stored under plans/{planId} (content only — no owner/member meta).
   Arrays with stable ids become id-keyed maps + order; coarse atoms pass through
   whole; per-viewer ui/collapsed are dropped. */
export function stateToContent(state) {
  const s = hydrateState(state);
  const cast = listToMap(s.cast);
  const rel = listToMap(s.rel);
  const factions = listToMap(s.factions);
  const links = listToMap(s.dynasty.links);
  return {
    core: clone(s.core),
    fields: clone(s.fields),
    cast: cast.map,
    castOrder: cast.order,
    rel: rel.map,
    relOrder: rel.order,
    factions: factions.map,          // each faction's members[] kept as-is (coarse)
    factionOrder: factions.order,
    dynasty: {
      links: links.map,
      linkOrder: links.order,
      allegiance: clone(s.dynasty.allegiance),   // coarse whole map (house-name keys)
      houseColors: clone(s.dynasty.houseColors || {}), // coarse whole map (house-name keys)
    },
    beatData: clone(s.beatData),     // coarse per-act arrays
    shape: s.shape,
    housesEnabled: s.housesEnabled !== false, // plain scalar → single scoped field write
  };
}

/* ── CONTENT → STATE ─────────────────────────────────────────────────────────
   Rebuild the in-memory shape the component renders. Re-adds fresh per-viewer
   ui/collapsed (they were never stored). Tolerant of empty/partial content. */
export function contentToState(content) {
  const c = (content && typeof content === 'object' && !Array.isArray(content)) ? content : {};
  const d = defaultPlanState();
  const dyn = (c.dynasty && typeof c.dynasty === 'object') ? c.dynasty : {};

  const cast = mapToList(c.cast, c.castOrder);
  const rel = mapToList(c.rel, c.relOrder);
  const factions = mapToList(c.factions, c.factionOrder);
  const links = mapToList(dyn.links, dyn.linkOrder);

  return {
    core: { ...d.core, ...(c.core || {}) },
    fields: { ...(c.fields || {}) },
    cast: cast.length ? cast : d.cast,
    rel: rel.length ? rel : d.rel,
    factions: factions.length ? factions : d.factions,
    dynasty: {
      links,
      allegiance: (dyn.allegiance && typeof dyn.allegiance === 'object') ? clone(dyn.allegiance) : {},
      houseColors: (dyn.houseColors && typeof dyn.houseColors === 'object') ? clone(dyn.houseColors) : {},
    },
    beatData: { ...(c.beatData || {}) },
    shape: SHAPES.includes(c.shape) ? c.shape : 'standalone',
    housesEnabled: c.housesEnabled !== false, // absent → on
    // per-viewer view state — defaulted fresh, not loaded from the shared doc:
    ui: { examples: true },
    collapsed: {},
  };
}

/* ── Legacy migration ────────────────────────────────────────────────────────
   Convert an existing plannerProgress `plan` blob (arrays, may carry
   ui/collapsed) into the new content shape. Used by the future
   seedPlanFromLegacy callable; kept here so the transform is tested in one place
   and the server can mirror it. */
export function legacyToContent(legacyPlan) {
  return stateToContent(hydrateState(legacyPlan));
}

/* Blank content for a brand-new plan (what createPlan will seed). */
export function emptyPlanContent() {
  return stateToContent(defaultPlanState());
}

/* Full new plan document (content + ownership meta), minus server timestamps
   which the callable stamps with serverTimestamp(). memberUids is intentionally
   absent — v1 gates purely on memberEmails (sketch §5.4). This is the canonical
   shape the createPlan callable will mirror server-side. */
export function newPlanDoc({ ownerUid, ownerEmail = '', title = '' } = {}) {
  const content = emptyPlanContent();
  return {
    ownerUid: ownerUid || null,
    ownerEmail: String(ownerEmail || '').toLowerCase(),
    memberEmails: [],
    title: (title && title.trim()) || (content.core.title || '').trim() || 'Untitled plan',
    ...content,
  };
}

/* Denormalised picker title kept in sync when core.title changes. */
export function planTitle(content) {
  const t = content && content.core && content.core.title;
  return (t && t.trim()) || 'Untitled plan';
}
